import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CustomDropdownButton from "global/components/CustomDropdownButton/CustomDropdownButton";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import { usePopover } from "hooks/use-popover";
import { Path } from "../interface";
import templatesStyles from "../Templates.styles";
import templatesTokens, {
  templatesResponsiveTokens,
} from "../Templates.tokens";
import TemplateBreadcrumb from "./TemplateBreadcrumb";
import {
  GridViewGlyph,
  ListViewGlyph,
  PlusCircleGlyph,
} from "./TemplatesIcons";
import { tourAttr } from "tour";

const styles = templatesStyles;
const { viewToggle, primaryButton } = templatesTokens;
const { phone, tablet } = templatesResponsiveTokens;

type View = "grid" | "list";

interface ItemSearchProps {
  path: Path[];
  setDialogType: (value: string) => void;
  setOpenDialog: (value: boolean) => void;
  setNewFolderName: (value: string) => void;
  onBreadcrumbClick?: (index: number) => void;
  onHomeClick?: () => void;
  isLoading: boolean;
  onViewChange: (view: View) => void;
  view: View;
  handleTemplateClick: () => void;
  handleOpenFolderClick: () => void;
  isShowCreateBtn: boolean;
  isAtTemplateLimit?: boolean;
  isMobile?: boolean;
  foldersEnabled?: boolean;
  foldersDisabledTooltip?: string;
  /** The design drops the trail and the actions on a fresh, empty account. */
  showBreadcrumb?: boolean;
  showActions?: boolean;
  /**
   * Drops the "Templates" title/strapline block — the picker embedded in
   * `AppModal` (`isUseTemplate`) already gets its title from the modal's own
   * header, so repeating it here would show it twice.
   */
  hideTitle?: boolean;
  /** The Template library tab keeps "Create" but has no grid / list view. */
  showViewToggle?: boolean;
}

/**
 * Page title, the two header actions (grid / list toggle and the "Create" split
 * button) and the folder breadcrumb trail beneath them.
 *
 * Below `sm` the reference drops the title (the app bar carries it) and the
 * view toggle, and replaces the split button with a single "Create" control —
 * a square button beside the subtitle at 390, a floating pill at 375. Both
 * open the same Template / Folder menu the split button's caret does, so
 * nothing becomes unreachable on a phone.
 */
const TemplateHeader = (props: ItemSearchProps) => {
  const { isLoading = false, onViewChange, view = "grid" } = props;
  const createMenu = usePopover<HTMLElement>();

  const iconSx = {
    width: { sm: tablet.viewToggleIconSize, md: viewToggle.iconSize },
    height: { sm: tablet.viewToggleIconSize, md: viewToggle.iconSize },
  };

  const {
    showBreadcrumb = true,
    showActions = true,
    hideTitle = false,
    showViewToggle = true,
  } = props;

  const canCreate =
    props.isShowCreateBtn &&
    hasAccessTo(strings.resources.template, strings.addPermission);

  const templateDisabled = isLoading || !!props.isAtTemplateLimit;
  const folderDisabled = isLoading || props.foldersEnabled === false;

  /** Template / Folder chooser shared by the two phone-only Create controls. */
  const mobileCreateMenu = (
    <Menu
      anchorEl={createMenu.anchorRef.current}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      open={createMenu.open}
      onClose={createMenu.handleClose}
    >
      <MenuItem
        disabled={templateDisabled}
        onClick={() => {
          createMenu.handleClose();
          props.handleTemplateClick();
        }}
      >
        <DescriptionOutlinedIcon
          sx={{ fontSize: 20, mr: 1.25, color: "text.secondary" }}
        />
        Template
      </MenuItem>
      <MenuItem
        disabled={folderDisabled}
        onClick={() => {
          createMenu.handleClose();
          props.handleOpenFolderClick();
        }}
      >
        <FolderOutlinedIcon
          sx={{ fontSize: 20, mr: 1.25, color: "text.secondary" }}
        />
        Folder
        {props.foldersEnabled === false && (
          <LockOutlinedIcon
            sx={{ fontSize: 16, color: "text.secondary", ml: "auto" }}
          />
        )}
      </MenuItem>
    </Menu>
  );

  return (
    <>
      <Box sx={styles.header(hideTitle)}>
        {!hideTitle && (
          <Box sx={{ minWidth: 0 }} {...tourAttr("templates.header")}>
            <Typography component="h1" sx={styles.headerTitle}>
              Templates
            </Typography>
            {/*
              Two copies of the strapline, not one that wraps: the 375 column
              of the reference runs the full sentence over two lines, while
              390 shortens it to make room for the square Create button on
              the same row. The tablet and desktop take the full sentence
              again.
            */}
            <Typography component="p" sx={styles.headerSubtitleLong}>
              Reusable documents and folders — set them up once, send them in
              seconds.
            </Typography>
            <Typography component="p" sx={styles.headerSubtitleShort}>
              {phone.subtitleShort}
            </Typography>
          </Box>
        )}

        {showActions && (
          <Box sx={styles.headerActions}>
            {showViewToggle && (
              <Box role="group" aria-label="View" sx={styles.viewToggle}>
                <IconButton
                  disableRipple
                  disabled={isLoading}
                  aria-label="Grid view"
                  aria-pressed={view === "grid"}
                  onClick={() => onViewChange?.("grid")}
                  sx={styles.viewToggleButton(view === "grid")}
                >
                  <GridViewGlyph sx={iconSx} />
                </IconButton>
                <IconButton
                  disableRipple
                  disabled={isLoading}
                  aria-label="List view"
                  aria-pressed={view === "list"}
                  onClick={() => onViewChange?.("list")}
                  sx={styles.viewToggleButton(view === "list")}
                >
                  <ListViewGlyph sx={iconSx} />
                </IconButton>
              </Box>
            )}

            {canCreate && (
              <>
                <Tooltip
                  title={
                    props.isAtTemplateLimit
                      ? "You've used all template slots in your plan. Upgrade to create more."
                      : ""
                  }
                  arrow
                  describeChild
                >
                  {/* Tablet and desktop keep the split button. */}
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}
                    {...tourAttr("templates.create")}
                  >
                    <CustomDropdownButton
                      buttonLabel="Create"
                      handleMainClick={
                        props.isAtTemplateLimit
                          ? undefined
                          : props.handleTemplateClick
                      }
                      startIcon={
                        <PlusCircleGlyph
                          sx={{
                            width: primaryButton.iconSize,
                            height: primaryButton.iconSize,
                          }}
                        />
                      }
                      menuItems={[
                        {
                          icon: (
                            <DescriptionOutlinedIcon
                              sx={{
                                fontSize: 20,
                                color: props.isAtTemplateLimit                                  ? "action.disabled"
                                  : "text.secondary",
                              }}
                            />
                          ),
                          label: "Template",
                          disabled: props.isAtTemplateLimit,
                          onClick: props.isAtTemplateLimit
                            ? () => {}
                            : props.handleTemplateClick,
                        },
                        {
                          icon: (
                            <FolderOutlinedIcon
                              sx={{
                                fontSize: 20,
                                color:
                                  props.foldersEnabled === false
                                    ? "action.disabled"
                                    : "text.secondary",
                              }}
                            />
                          ),
                          label: "Folder",
                          disabled: props.foldersEnabled === false,
                          onClick:
                            props.foldersEnabled === false
                              ? () => {}
                              : props.handleOpenFolderClick,
                          endIcon:
                            props.foldersEnabled === false ? (
                              <LockOutlinedIcon
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                            ) : undefined,
                          tooltip:
                            props.foldersEnabled === false
                              ? (props.foldersDisabledTooltip ??
                                strings.FEATURE_GATE_UPSELL_TOOLTIP)
                              : undefined,
                        },
                      ]}
                      disabled={isLoading}
                    />
                  </Box>
                </Tooltip>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Trail sits under the title block, as on Sign Requests. */}
      {showBreadcrumb && (
        <TemplateBreadcrumb
          path={props.path}
          onBreadcrumbClick={props.onBreadcrumbClick}
          onHomeClick={props.onHomeClick}
          isLoading={isLoading}
        />
      )}

      {/*
        Phone "Create" — full width under the header, replacing the square
        button the reference put beside the subtitle and the pill it floated
        over the list. It opens the same Template / Folder chooser.
      */}
      {showActions && canCreate && (
        <Button
          disableRipple
          disabled={isLoading}
          aria-haspopup="menu"
          aria-expanded={createMenu.open}
          sx={styles.createMobile}
          {...tourAttr("templates.create")}
          onClick={(event) => {
            createMenu.anchorRef.current = event.currentTarget;
            createMenu.handleOpen();
          }}
        >
          <PlusCircleGlyph
            sx={{
              width: phone.createButton.iconSize,
              height: phone.createButton.iconSize,
            }}
          />
          Create
        </Button>
      )}

      {showActions && canCreate && mobileCreateMenu}
    </>
  );
};

export default TemplateHeader;
